// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a machine's settings that live in Konvo, on its card in Settings → Connections → Agents
// (design/agents-team-threads m6-machine-sharing; JP 2026-10-15: nothing to run on the machine).
// Its owner turns "Allow and Deny from Konvo" on or off (for them and teammates alike, in the app and
// on the lock screen; off for a new machine) and picks how far its threads may be shared (Prompt for a
// new machine), sees what is shared now, and can stop all sharing.

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import styles from '@app/features/konvo_agents/components/AgentShare.module.css';
import {
	canName,
	Face,
	LevelIcon,
	levelName,
	shareErrorMessage,
} from '@app/features/konvo_agents/components/AgentShareBits';
import AgentShares from '@app/features/konvo_agents/state/AgentShares';
import Navigation from '@app/features/navigation/state/Navigation';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {
	AGENT_MACHINE_SHARING,
	type AgentMachineSharing as SharingLevel,
} from '@fluxer/schema/src/domains/konvo/AgentShares';
import type {AgentMachineView} from '@fluxer/schema/src/domains/konvo/Agents';
import {plural} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {IconLock} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

export const AgentMachineSharing = observer(({machine}: {machine: AgentMachineView}) => {
	const {i18n, t} = useLingui();
	const [open, setOpen] = useState(false);
	const [saving, setSaving] = useState(false);
	useEffect(() => {
		void AgentShares.loadOwner();
	}, []);
	const cap: SharingLevel = machine.limits.share ?? 'off';
	const approve = machine.limits.approve;
	const mine = AgentShares.owner?.machines.find((entry) => entry.machineId === machine.id);
	const threads = mine?.threads ?? [];
	const people = new Map<string, string>();
	for (const thread of threads) for (const person of thread.people) people.set(person.userId, person.name);
	const machineName = machine.name;

	const save = async (patch: {share?: SharingLevel; approve?: boolean}) => {
		setSaving(true);
		try {
			await AgentShares.saveMachineSettings(machine.id, patch);
		} catch (error) {
			ToastCommands.error(shareErrorMessage(i18n, error));
		} finally {
			setSaving(false);
		}
	};
	const pickCap = (next: SharingLevel) => {
		if (next === cap) return;
		if (next !== 'off' || threads.length === 0) {
			void save({share: next});
			return;
		}
		ModalCommands.push(
			ModalCommands.modal(() => (
				<ConfirmModal
					title={t({
						message: `Turn team sharing off for ${machineName}?`,
						comment: 'Agents Settings: confirm switching team sharing off while threads are shared.',
					})}
					description={t({
						message:
							'Everyone you shared a thread with loses it at once. Their comments stay with the threads for you.',
						comment: 'Agents Settings: what switching team sharing off does.',
					})}
					primaryText={t({message: 'Turn off', comment: 'Agents Settings: switch team sharing off.'})}
					primaryVariant="danger"
					onPrimary={() => save({share: 'off'})}
				/>
			)),
		);
	};

	const stopAll = () => {
		ModalCommands.push(
			ModalCommands.modal(() => (
				<ConfirmModal
					title={t({
						message: `Stop sharing ${machineName}'s threads?`,
						comment: 'Agents Settings: confirm ending every team share of a machine’s threads.',
					})}
					description={t({
						message:
							'Everyone you shared a thread with loses it at once. Their comments stay with the threads for you. Team sharing itself stays on.',
						comment: 'Agents Settings: what Stop all sharing does.',
					})}
					primaryText={t({message: 'Stop all sharing', comment: 'Agents Settings: end every team share of a machine.'})}
					primaryVariant="danger"
					onPrimary={async () => {
						try {
							await AgentShares.stopAll(machine.id);
							ToastCommands.success(
								t({message: 'Sharing stopped.', comment: 'Agents Settings: toast after Stop all sharing.'}),
							);
						} catch (error) {
							ToastCommands.error(shareErrorMessage(i18n, error));
						}
					}}
				/>
			)),
		);
	};

	return (
		<dl className={clsx(styles.scope, styles.settings)} data-flx="konvo-agents.machine.sharing">
			<dt>
				<Trans comment="Agents Settings: row with the switch that lets Allow / Deny in Konvo answer a machine's approval requests.">
					Allow and Deny
				</Trans>
			</dt>
			<dd>
				<div data-flx="konvo-agents.machine.approve">
					<Switch
						label={t({
							message: 'Allow and Deny from Konvo',
							comment: 'Agents Settings: switch that lets Konvo answer the machine’s approval requests.',
						})}
						description={t({
							message:
								'An Allow in Konvo, on your phone’s lock screen or by someone you share a thread with at Prompt runs that command on this machine.',
							comment: 'Agents Settings: what the Allow and Deny switch means.',
						})}
						value={approve}
						disabled={saving}
						onChange={(value) => void save({approve: value})}
						compact
					/>
				</div>
			</dd>
			<dt>
				<Trans comment="Agents Settings: row about sharing a machine's threads with teammates.">Team sharing</Trans>
			</dt>
			<dd>
				<div className={styles.capPills} role="radiogroup" data-flx="konvo-agents.machine.sharing.cap">
					{AGENT_MACHINE_SHARING.map((level) => (
						<button
							key={level}
							type="button"
							role="radio"
							aria-checked={cap === level}
							className={styles.pill}
							disabled={saving}
							onClick={() => pickCap(level)}
							data-flx={`konvo-agents.machine.sharing.cap.${level}`}
						>
							{level === 'off' ? <IconLock size={14} stroke={1.8} /> : <LevelIcon level={level} size={14} />}
							{level === 'off'
								? t({message: 'Off', comment: 'Agents Settings: team sharing switched off.'})
								: levelName(i18n, level)}
						</button>
					))}
				</div>
				<span className={styles.settingsHint}>
					{cap === 'off' ? (
						<Trans comment="Agents Settings: team sharing is off for this machine.">
							Nobody else sees {machineName}'s threads.
						</Trans>
					) : (
						<Trans comment="Agents Settings: how far threads may be shared; each thread is shared from its own header.">
							You share single threads from their header, up to this. {machineName}'s own limits still apply.
						</Trans>
					)}
				</span>
			</dd>
			{(cap !== 'off' || threads.length > 0) && (
				<>
					<dt>
						<Trans comment="Agents Settings: what is shared with teammates right now.">Shared now</Trans>
					</dt>
					<dd>
						<div className={styles.settingsNow}>
							<span className={styles.settingsSummary} data-flx="konvo-agents.machine.sharing.now">
								{people.size > 0 && (
									<span className={styles.faces}>
										{[...people.entries()].slice(0, 4).map(([userId, name]) => (
											<Face key={userId} userId={userId} name={name} size={24} />
										))}
									</span>
								)}
								{threads.length === 0
									? t({message: 'Nothing yet', comment: 'Agents Settings: no thread of this machine is shared.'})
									: plural(threads.length, {one: '# thread', other: '# threads'})}
							</span>
							{threads.length > 0 && (
								<>
									<button
										type="button"
										className={styles.secondary}
										onClick={() => setOpen(!open)}
										aria-expanded={open}
										data-flx="konvo-agents.machine.sharing.manage"
									>
										<Trans comment="Agents Settings: show which threads are shared, with whom.">Manage</Trans>
									</button>
									<button
										type="button"
										className={clsx(styles.secondary, styles.danger)}
										onClick={stopAll}
										data-flx="konvo-agents.machine.sharing.stop"
									>
										<Trans comment="Agents Settings: end every team share of this machine's threads.">
											Stop all sharing
										</Trans>
									</button>
								</>
							)}
						</div>
						{open && (
							<ul className={styles.settingsList} data-flx="konvo-agents.machine.sharing.list">
								{threads.map((thread) => (
									<li key={thread.threadId}>
										<button
											type="button"
											className={styles.settingsThread}
											onClick={() => Navigation.navigateToAgents(machine.id, thread.threadId)}
										>
											{thread.title || thread.threadId}
										</button>
										<span className={styles.settingsPeople}>
											{thread.people.map((person) => `${person.name} (${canName(i18n, person.level)})`).join(', ')}
										</span>
									</li>
								))}
							</ul>
						)}
					</dd>
				</>
			)}
		</dl>
	);
});
